import type React from 'react';
import type { ComponentPropsWithoutRef, Ref } from 'react';
import { useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Observable } from 'rxjs';
import { cn } from '../../common/functions/cn';
import useTestTag from '../../common/hooks/use-test-tag';
import { ButtonV2 } from '../button-v2/button-v2';
import type { ModalDialogButtonSetting } from '../common-interfaces';
import OdinIcon from '../odin-icon/odin-icon.component';
import { Spinner } from '../spinner/spinner';

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

export interface DialogProps extends Omit<ComponentPropsWithoutRef<'dialog'>, 'onClose'> {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType?: 'button' | 'submit';
  containerClassName?: string;
  contentClassName?: string;
  footer?: string | React.JSX.Element;
  fullScreen?: boolean;
  header?: string | React.JSX.Element;
  loading?: boolean;
  ref?: Ref<DialogRef>;
  showCloseIcon?: boolean;
  visible?: boolean;
  onClose?: (returnValue?: string) => void;
  closeOnClickOutside?: boolean;
}

interface DialogCloseButtonProps {
  onClose: () => void;
}

const DialogCloseButton = ({ onClose }: DialogCloseButtonProps) => {
  const { t } = useTranslation();

  return (
    <ButtonV2
      variant="ghost"
      size="icon"
      aria-label={t('close')}
      title={t('close')}
      onClick={onClose}
      data-testid="modal-close-icon"
    >
      <OdinIcon icon="X" />
    </ButtonV2>
  );
};

interface DialogHeaderProps {
  header?: string | React.JSX.Element;
  showCloseIcon: boolean;
  onClose: () => void;
}

const DialogHeader = ({ header, showCloseIcon, onClose }: DialogHeaderProps) => (
  <div className="flex shrink-0 items-center gap-2 pb-2">
    <div className="grow truncate font-medium text-base">{header}</div>
    {showCloseIcon && <DialogCloseButton onClose={onClose} />}
  </div>
);

interface DialogFooterButtonProps {
  button: ModalDialogButtonSetting;
  type: 'button' | 'submit';
  onClose: (returnValue?: string) => void;
}

const DialogFooterButton = ({ button, type, onClose }: DialogFooterButtonProps) => {
  const tagCreator = useTestTag('modal');
  const { text, classNames, closeModalOnClick, onClick, ...buttonProps } = button;

  return (
    <ButtonV2
      {...buttonProps}
      ref={tagCreator(`${text.replace(/\s+/g, '-').toLowerCase()}-button`)}
      type={type}
      className={classNames}
      onClick={() => {
        onClick?.();
        if (closeModalOnClick) {
          onClose(buttonProps.name);
        }
      }}
    >
      {text}
    </ButtonV2>
  );
};

interface DialogFooterProps {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType: 'button' | 'submit';
  footer?: string | React.JSX.Element;
  onClose: (returnValue?: string) => void;
}

const DialogFooter = ({ buttons, buttonType, footer, onClose }: DialogFooterProps) => {
  if (!buttons && !footer) return null;

  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-solid pt-4 sm:flex-row sm:items-center sm:justify-between">
      {footer && <div className="min-w-0 flex-1 truncate p-1">{footer}</div>}
      {buttons && (
        <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row sm:items-center">
          {buttons.map((button) => (
            <DialogFooterButton key={button.name} button={button} type={buttonType} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
};

function Dialog({
  buttons,
  buttonType = 'button',
  children,
  className,
  containerClassName,
  contentClassName,
  footer,
  fullScreen = false,
  header,
  id,
  loading = false,
  ref,
  showCloseIcon = true,
  visible = false,
  onClose,
  closeOnClickOutside = true,
  ...props
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tagCreator = useTestTag('modal');
  const onCloseCalledRef = useRef(false);

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible]);

  const show = () => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  };

  const close = useCallback(
    (returnValue?: string) => {
      const dialog = dialogRef.current;
      if (dialog?.open) {
        // Set returnValue before calling close() to ensure it's captured correctly
        if (returnValue) {
          dialog.returnValue = returnValue;
        }
        dialog.close();
        // Call onClose immediately to ensure parent state is updated without waiting for the native 'close' event.
        // Set the flag AFTER calling onClose to prevent handleNativeClose from calling it again.
        // This handles cases where dialog.close() triggers the native 'close' event synchronously.
        onClose?.(returnValue);
        onCloseCalledRef.current = true;
      } else {
        // If already closed, just notify parent (only if not already called)
        if (!onCloseCalledRef.current) {
          onCloseCalledRef.current = true;
          onClose?.(returnValue);
        }
      }
    },
    [onClose]
  );

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const handleDialogMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    // event.button === 0 ensures only primary (left) clicks close the dialog, ignoring right-click and middle-click
    if (closeOnClickOutside && event.button === 0 && event.target === dialogRef.current) {
      close('overlay');
    }
  };

  const handleNativeClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // Without this, closing a nested dialog will also close any parent dialog.
      event.stopPropagation();

      // This fires when the dialog is closed by ANY means (ESC, form method="dialog", dialog.close())
      // Only call onClose if it hasn't been called already (e.g., from the close() method)
      // This prevents duplicate calls that could trigger side effects twice.
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        const returnValue = dialogRef.current?.returnValue;
        onClose?.(returnValue);
      }
    },
    [onClose]
  );

  const containerStyles = cn(
    'flex max-h-[calc(100svh-2rem)] flex-col p-4',
    {
      'h-[calc(100svh-2rem)] w-[calc(100svw-2rem)]': fullScreen,
    },
    containerClassName
  );

  const contentStyles = cn('min-h-0 flex-1 overflow-y-auto overflow-x-clip py-4', contentClassName);

  const dialogStyles = cn(
    'm-auto overflow-visible rounded-md bg-popover text-popover-foreground outline-hidden [&:not([open])]:pointer-events-none',
    className
  );

  const createDialog = () => (
    <dialog
      ref={dialogRef}
      id={id}
      className={dialogStyles}
      onMouseDown={handleDialogMouseDown}
      onClose={handleNativeClose}
      {...props}
    >
      {loading && (
        <div className="absolute z-10 grid h-full w-full place-content-center rounded-md bg-popover/40">
          <Spinner className="size-6" />
        </div>
      )}
      <div className={containerStyles} ref={tagCreator('container')}>
        <DialogHeader header={header} showCloseIcon={showCloseIcon} onClose={() => close('close-icon')} />
        <div className={contentStyles} ref={tagCreator('content')}>
          {children}
        </div>
        <DialogFooter buttons={buttons} buttonType={buttonType} footer={footer} onClose={close} />
      </div>
    </dialog>
  );

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default Dialog;
